import React,{useEffect,useMemo,useRef,useState} from 'react';
import {FiDownload,FiGlobe,FiInfo,FiRefreshCw} from 'react-icons/fi';
import Dialog from './Dialog';
import {resolveApiBase} from '../lib/transcriptionApi.js';
import {downloadFile} from '../lib/exports.js';
import {TRANSLATION_LANGUAGES,translationCues,cueBatches,
 makeTranslatedSrt,makeBilingualSrt,translationProjectForScope} from '../lib/translation.js';
import './TranslationDialog.css';

export default function TranslationDialog({project,sourceLabel,selectedPassageId=null,getAccessToken,onClose}){
 const [language,setLanguage]=useState('es');
 const [scope,setScope]=useState('assembly');
 const [translations,setTranslations]=useState({});
 const [busy,setBusy]=useState(false);
 const [progress,setProgress]=useState(0);
 const [error,setError]=useState('');
 const [completed,setCompleted]=useState(false);
 const abortRef=useRef(null);
 const [review,setReview]=useState('side-by-side');
 const chosenPassage=selectedPassageId&&project.paperExport?.blocks?.find(block=>block.id===selectedPassageId);
 const translationProject=useMemo(
  ()=>translationProjectForScope(project,scope,selectedPassageId),
  [project,scope,selectedPassageId]
 );
 const computation=useMemo(()=>{
  try{return {cues:translationCues(translationProject),error:''};}
  catch(e){return {cues:[],error:e.message};}
 },[translationProject]);
 const cues=computation.cues;
 const count=cues.filter(c=>translations[c.id]?.trim()).length;
 const ready=completed&&count===cues.length&&cues.length>0;
 useEffect(()=>()=>abortRef.current?.abort(),[]);
 const changeScope=next=>{
  if(busy)return;
  setScope(next);setTranslations({});setProgress(0);
  setCompleted(false);setError('');
 };
 const changeLanguage=next=>{
  if(busy)abortRef.current?.abort();
  setLanguage(next);setTranslations({});setProgress(0);
  setCompleted(false);setError('');
 };
 const translate=async()=>{
  if(!cues.length||busy)return;
  const controller=new AbortController();abortRef.current=controller;
  const batches=cueBatches(cues);
  setTranslations({});setBusy(true);setCompleted(false);setError('');setProgress(0);
  const translated={};
  try{
   const token=await getAccessToken();
   for(let index=0;index<batches.length;index++){
    if(controller.signal.aborted)throw new DOMException('Translation cancelled','AbortError');
    const response=await fetch(resolveApiBase()+'/api/translate-subtitles',{
     method:'POST',
     headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
     signal:controller.signal,
     body:JSON.stringify({language,cues:batches[index]})
    });
    const data=await response.json().catch(()=>({}));
    if(!response.ok)throw new Error(data.error||'AI translation could not complete.');
    const expected=new Set(batches[index].map(c=>c.id));
    if(!Array.isArray(data.translations)||data.translations.length!==expected.size||
      data.translations.some(t=>!expected.has(t.id)||typeof t.text!=='string'||!t.text.trim())||
      new Set(data.translations.map(t=>t.id)).size!==expected.size)
     throw new Error('AI returned incomplete subtitle lines. Please retry.');
    for(const item of data.translations)translated[item.id]=item.text.trim();
    setTranslations({...translated});
    setProgress(Math.round((index+1)/batches.length*100));
   }
   setCompleted(true);
  }catch(e){
   if(e.name!=='AbortError')setError(e.message||'Translation failed. Please retry.');
  }finally{
   if(abortRef.current===controller)abortRef.current=null;
   setBusy(false);
  }
 };
 const download=kind=>{
  try{
   const label=TRANSLATION_LANGUAGES.find(l=>l[0]===language)?.[1]||language;
   const slug=(sourceLabel||'takefold-edit').replace(/[^a-z0-9_-]+/gi,'-').slice(0,55).toLowerCase()+(scope==='passage'?'-passage':'');
   const contents=kind==='bilingual'?makeBilingualSrt(cues,translations):makeTranslatedSrt(cues,translations);
   downloadFile(`${slug}-${language}${kind==='bilingual'?'-bilingual':''}.srt`,
    contents,'text/plain;charset=utf-8');
   setError('');
  }catch(e){setError(e.message);}
 };
 return <Dialog title="Translate subtitles" onClose={()=>{
  abortRef.current?.abort();onClose();
 }} className="translation-dialog">
  <div className="translation-intro"><FiGlobe aria-hidden="true"/>
   Translate the selected edit into another language's subtitles. Original dialogue, video, cuts and timecodes stay unchanged.
  </div>
  {chosenPassage&&<div className="translation-scope-selector" role="group" aria-label="Translate scope">
    <button type="button" aria-pressed={scope==='assembly'} disabled={busy}
      onClick={()=>changeScope('assembly')}>Entire Assembly</button>
    <button type="button" aria-pressed={scope==='passage'} disabled={busy}
      onClick={()=>changeScope('passage')}>Selected passage</button>
   </div>}
  <div className="translation-top-controls">
   <label htmlFor="translation-language">Translate to
    <select id="translation-language" value={language} onChange={e=>changeLanguage(e.target.value)}
     disabled={busy}>{TRANSLATION_LANGUAGES.map(([id,name])=><option value={id} key={id}>{name}</option>)}</select>
   </label>
   <button type="button" onClick={translate} disabled={busy||!!computation.error}>
    <FiRefreshCw aria-hidden="true"/> {busy?'Translating…':completed?'Translate again':'Translate'}
   </button>
  </div>
  <div className="translation-scope">
   <strong title={sourceLabel}>{sourceLabel}</strong>
   <span>{cues.length} timed lines · {count} translated</span>
  </div>
  {busy&&<div className="translation-progress" role="status">Translating {progress}%
   <div><span style={{width:progress+'%'}}/></div>
  </div>}
  {(error||computation.error)&&<p className="translation-error" role="alert">{error||computation.error}</p>}
  {!completed&&!busy&&cues.length>0&&<p className="translation-hint">
   Gemini translates the spoken dialogue. Review names, technical terms and reading speed before delivery.
  </p>}
  {cues.length>0&&<div className="translation-review">
   <div className="translation-review-head">
    <strong>Subtitle review</strong>
    <div role="group" aria-label="Translation preview">
     <button type="button" aria-pressed={review==='side-by-side'} onClick={()=>setReview('side-by-side')}>Original + translation</button>
     <button type="button" aria-pressed={review==='translation'} onClick={()=>setReview('translation')}>Translation only</button>
    </div>
   </div>
   <div className="translation-cue-list" aria-label="Timed dialogue and editable translations">
    {cues.map((cue,index)=><div className="translation-cue" key={cue.id}>
     <span className="translation-index">{String(index+1).padStart(2,'0')} · {cue.start.toFixed(1)}s</span>
     <div className="translation-cue-body">
      {review==='side-by-side'&&<p className="translation-original">{cue.text}</p>}
      {completed||busy?<textarea aria-label={'Translated subtitle line '+(index+1)}
       rows={Math.max(2,Math.min(4,Math.ceil((translations[cue.id]||'').length/68)))}
       maxLength={850} value={translations[cue.id]||''} disabled={busy}
       placeholder={busy?'Translating…':'Enter or correct translation'}
       onChange={e=>setTranslations(prev=>({...prev,[cue.id]:e.target.value}))}/>
       :review==='translation'?<p className="translation-placeholder">Translate to preview this line.</p>:null}
     </div>
    </div>)}
   </div>
  </div>}
  <div className="translation-foot">
   <span><FiInfo aria-hidden="true"/> Text/subtitles only — no voice dubbing.</span>
   <div>
    <button disabled={!ready||busy} onClick={()=>download('bilingual')}><FiDownload/> Bilingual SRT</button>
    <button disabled={!ready||busy} className="translation-primary" onClick={()=>download('srt')}><FiDownload/> Translated SRT</button>
   </div>
  </div>
 </Dialog>;
}
